import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { IHasTestData } from '../../../ui-toolkit/test-utilities/IHasTestData';
import { SummaryContext, SummarySetterContext, TabProps } from './summaryContext';
import { mergeTests } from '../../../ui-toolkit/test-utilities/mergeTests';
import { Tabs } from '@zillow/constellation-10';

interface SectionTabsProps extends IHasTestData {
  tabs?: TabProps[];
}
export const SectionTabs: React.FC<SectionTabsProps> = ({ tabs, ...rest }) => {
  const { activeTab } = useContext(SummaryContext);
  const setSummaryState = useContext(SummarySetterContext);

  const availableTabs = useMemo(() => tabs ?? [], [tabs]);

  useEffect(() => {
    if (availableTabs.length === 0) {
      return;
    }

    const currentLabel = activeTab?.label;
    const currentExists = currentLabel && availableTabs.some((tab) => tab.label === currentLabel);

    if (!currentExists) {
      const nextTab = availableTabs[0];
      setSummaryState(() => ({
        activeTab: nextTab,
        filterText: '',
      }));
    }
  }, [availableTabs, activeTab, setSummaryState]);

  const selectedValue = activeTab?.label;

  const handleSelectedChange = useCallback(
    (value: string) => {
      if (availableTabs.length === 0) {
        return;
      }

      const nextTab = availableTabs.find((tab) => tab.label === value) ?? activeTab ?? availableTabs[0];
      if (!nextTab || nextTab.label === activeTab?.label) {
        return;
      }
      setSummaryState(() => ({
        activeTab: nextTab,
        filterText: '',
      }));
    },
    [availableTabs, activeTab, setSummaryState]
  );

  if (availableTabs.length === 0) {
    return null;
  }

  return (
    <Tabs.Root
      appearance="default"
      fluid
      selected={selectedValue}
      onSelectedChange={handleSelectedChange}
      data-test-id={mergeTests(rest['data-test-id'], 'section-tabs')}
    >
      <Tabs.List>
        {availableTabs.map((tab) => (
          <Tabs.Tab
            key={tab.label}
            value={tab.label}
            css={{
              flex: '1 1 0%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '56px',
              fontWeight: 600,
            }}
            data-test-id={mergeTests(`${rest['data-test-id']}-${tab.label}`, 'summary-tab-cell')}
          >
            {tab.title}
          </Tabs.Tab>
        ))}
      </Tabs.List>
    </Tabs.Root>
  );
};
